import {
    DefaultButton, Dropdown, getId, Icon, IContextualMenuProps,
    Modal, SearchBox, Spinner, SpinnerSize
} from '@fluentui/react';
import * as React from 'react';
import { AdminProductLineService } from '../../services/AdminProductLineService';
import ProductFilterView from './ProductFilterView';
import { EditPictureModel } from '../../../pictures/models/PictureModel';
import useAdminProductVM from '../../viewmodels/AdminProductVM';
import { createProductCountByModel, ProductKeyModel, ProductSearchOption } from '../../models/ProductModel';
import { useCategoryMenuVM } from '../../viewmodels/CategoryMenuVM';
import { Utils } from '../../../common/views/Utils';
import { Page } from '../../../grid/models/Page';
import { Pager } from '../../../grid/views/Pager';
import { IPictureService } from '../../../pictures/viewmodels/PictureVM';
import { ProductUtils } from '../../ProductUtils';
import { MenuFilterModel, ProductMenuFilterOption } from '../../models/CategoryModel';
import ProductFilterTextView from './ProductFilterTextView';
import { StyleUtils } from '../../../common/views/StyleUtils';
import ProductDetailView from './ProductDetailView';
import NewOneTimeView from '../new/NewOneTimeView';
import ModalHeaderView from '../../../common/views/ModalHeaderView';
import NewProductView from '../new/NewProductView';
import ProductGridView from '../shared/ProductGridView';
import { addCartFunc } from '../../../carts/views/CartView';
import useFavoriteVM from '../../../favorites/viewmodels/FavoriteVM';
var Barcode = require('react-barcode');

export class ProductPictureService implements IPictureService {
    private _service = new AdminProductLineService();

    public create(ownerId: number, model: FormData): JQueryPromise<any> {
        return Utils.postFormData(model, this._service.RouteCreatePictures(ownerId));
    }

    public edit(ownerId: number, model: EditPictureModel): JQueryPromise<any> {
        return this._service.editPicture(ownerId, model);
    }

    public editList(ownerId: number, model: EditPictureModel[]): JQueryPromise<any> {
        return this._service.editPictures(ownerId, model);
    }

    public getList(ownerId: number): JQueryPromise<any> {
        return this._service.getPictures(ownerId);
    }

    public delete(ownerId: number, id: number): JQueryPromise<any> {
        return this._service.deletePicture(ownerId, id);
    }
}

export type ProductProps = {
    addToCart: addCartFunc;
}

export default function ProductView({ addToCart }: ProductProps) {
    const pictureService = new ProductPictureService();

    const hasLoaded = React.useRef(false);
    const vm = useAdminProductVM();
    const catVM = useCategoryMenuVM();
    const favoriteVM = useFavoriteVM();

    React.useEffect(() => {
        if (hasLoaded.current) return;

        hasLoaded.current = true;

        vm.onLoad();

        const menuOption: ProductMenuFilterOption = {
            hideBrand: false,
            hideGender: false,
            hideCategory: false
        };

        catVM.getList(menuOption);
    }, [vm, catVM]);

    //const isMobile = Utils.isMobile();
    const isMobile = window.innerWidth <= 700;
    const titleId: string = getId('title');
    const subtitleId: string = getId('subText');

    const onShowAddNew = () => {
        const count = createProductCountByModel();
        catVM.open([], count);
        vm.setShowAdd(true);
    }

    const onCloseAddNew = () => {
        catVM.close();
        vm.setShowAdd(false);
    }

    const createMenuItems = () => {
        const items = [];

        //if (userRoles.isDataEntry) {
        items.push({
            key: 'create',
            onClick: onShowAddNew,
            iconProps: { iconName: 'Add' },
            text: 'Create'
        });

        items.push({
            key: 'createOneTime',
            onClick: () => vm.setShowAddOneTime(true),
            iconProps: { iconName: 'Add' },
            text: 'Create OneTime'
        });

        //items.push({
        //    key: 'script',
        //    onClick: () => vm.setShowScript(true),
        //    iconProps: { iconName: 'Script' },
        //    text: 'Generate Filter Text'
        //});
        //}

        items.push({
            key: 'refresh',
            onClick: () => { vm.getList(false) },
            iconProps: { iconName: 'Refresh' },
            text: 'Refresh'
        });

        return items;
    }

    const commandMenuProps: IContextualMenuProps = {
        shouldFocusOnMount: true,
        items: createMenuItems()
    };

    const onShowFilter = () => {
        const count = createProductCountByModel();
        catVM.open(vm.filter.menuFilters, count);

        vm.tempSetFilter(vm.filter);
        vm.setShowFilter(true);
    };

    const onCloseFilter = () => {
        catVM.close();
        vm.setShowFilter(false);
    }

    const onSubmitFilter = () => {
        const selectedList = catVM.getSelectedList().map(
            (item) => {
                return { type: item.type, value: item.value, text: item.text } as MenuFilterModel;
            });

        const newFilter = vm.tempFilter;

        newFilter.menuFilters = selectedList;
        vm.tempSetFilterField("menuFilters", selectedList);

        vm.getListWithFilter(newFilter);
        onCloseFilter();
    }

    const onDetail = (key: ProductKeyModel) => {
        vm.setSelectedProduct(key);
        vm.setShowDetail(true);
    }

    const onCompletedDetail = () => {
        if (window.confirm("Would you like to refresh the data?")) {
            vm.getList();
        }

        vm.setShowDetail(false);
    }

    const onBarcode = (barcode: string) => {
        vm.setShowBarcode(true);
        vm.setBarcode(barcode);
    }

    const onCloseBarcode = () => {
        vm.setShowBarcode(false);
        vm.setBarcode("");
    }

    const onSearch = (value: string) => {
        vm.setFilterField("search", value);
        vm.getList();
    }

    const onSetFavorite = (lineId: number, isDeleted: boolean) => {
        const callback = (succeed: boolean) => {
            if (succeed) {
                vm.setLines(prev =>
                    prev.map(item => item.id === lineId ?
                        { ...item, isFavorite: !item.isFavorite } :
                        item
                    )
                );
            }
        }

        favoriteVM.set(lineId, isDeleted, false, callback);
    }

    const searchOptionPicker = () => {
        return <>
            <Dropdown
                label=""
                selectedKey={vm.filter.searchOption}
                style={{ width: 100, maxWidth: "100%" }}
                options={ProductUtils.getSearchOptions()}
                onChange={(_, v) => vm.setFilterField("searchOption", v?.key as ProductSearchOption)}
            />
        </>;
    }

    const onPageChange = (page: Page) => {
        if (page) {
            vm.filter.pageNumber = page.pageNumber;
            if (vm.filter.pageSize !== page.pageSize) vm.filter.pageNumber = 1;
            vm.filter.pageSize = page.pageSize;

            vm.getList(true);
        }
    }

    const headerDesktopView = () => {
        const width1 = Utils.getColumnWidth(1);
        const width3 = Utils.getColumnWidth(3);
        const width7 = Utils.getColumnWidth(7);

        return <div className="ms-Grid-row">
            <div className={width7 + " left"}>{filterTexts()}</div>

            <div className={width1}>
                {searchOptionPicker()}
            </div>

            <div className={width3 + " ph-0"}>
                {searchView()}
            </div>

            <div className={width1 + " right"}>
                <div style={{ display: "inline-flex" }}>
                    {settingButton()}
                    <span className="pl-10"></span>
                    <div style={{ display: "inline-block" }}>{filterButton()}</div>
                </div>
            </div>

        </div>;
    }

    const headerMobileView = () => {
        const width3 = Utils.getColumnWidth(3);
        const width6 = Utils.getColumnWidth(6);

        return <>
            <div className="ms-Grid-row ph-10">
                <div className={width3 + " left pr-5"}>
                    {searchOptionPicker()}
                </div>

                <div className={width6 + " pl-5 pr-10"}>
                    {searchView()}
                </div>

                <div className={width3 + " right pl-0"}>
                    <div style={{ display: "inline-flex" }}>
                        {settingButton()}
                        <div className="pl-10" style={{ display: "inline-block" }}>{filterButton()}</div>
                    </div>
                </div>
            </div>

            <div className="pt-20">{filterTexts()}</div>
        </>;
    }

    const settingButton = () => {
        return <>
            <DefaultButton
                menuProps={commandMenuProps}
                className="context-menu-text"
                styles={{
                    root: { height: 35, minWidth: 40, width: 40 },
                    menuIcon: { display: "none !important" }
                }}
                style={{ backgroundColor: "white", height: 32 }}>
                <Icon iconName="Settings" />
            </DefaultButton>
        </>;
    }

    const filterButton = () => {
        return <>
            <DefaultButton
                className="context-menu-text"
                onClick={onShowFilter}
                styles={{
                    root: { height: 35, minWidth: 40, width: 40 },
                    menuIcon: { display: "none !important" }
                }}
                style={{ backgroundColor: "white", height: 32 }}>
                <Icon iconName="Filter" />
            </DefaultButton>
        </>;
    }

    const searchView = () => {
        return <SearchBox
            autoFocus={false}
            value={vm.filter.search}
            placeholder="Search"
            onChange={(_, newValue) => vm.setFilterField("search", newValue || "")}
            onSearch={(value: string) => vm.getList(false)}
            onClear={() => vm.setFilterField("search", "")}
            className="searchbox"
            styles={{ root: { width: "100%" } }}
        />
    }

    const filterTexts = () => {
        return <>
            <ProductFilterTextView
                filter={vm.filter}
                sizeOptions={vm.sizeOptions}
                onChange={vm.getListWithFilter}
            />
        </>;
    }

    const addNewView = () => {
        return <>
            <Modal
                titleAriaId={titleId}
                subtitleAriaId={subtitleId}
                isOpen={true}
                onDismiss={onCloseAddNew}
                isBlocking={true}
                styles={StyleUtils.getModalWidthStyles(1500)}
            >
                <ModalHeaderView title="Create New Product" onClose={onCloseAddNew} />

                <div
                    id={subtitleId}
                    className="popup-body"
                    style={{ height: "90vh", overflowY: "auto" }}
                >
                    <NewProductView
                        brandOptions={vm.brandOptions}
                        sizeOptions={vm.sizeOptions}
                        categoryList={catVM.list}
                        getSelectedCategoryList={catVM.getSelectedList}
                        onCategoryChecked={catVM.setCheck}
                        onCategoryCollapsed={catVM.setCollapse}
                        onAction={onCloseAddNew}
                    />
                </div>
            </Modal>
        </>;
    }

    const addNewOneTimeView = () => {
        const title = "Create One Time";

        return <>
            <Modal
                titleAriaId={titleId}
                subtitleAriaId={subtitleId}
                isOpen={true}
                onDismiss={() => { vm.setShowAddOneTime(false) }}
                isBlocking={true}
                styles={StyleUtils.getModalWidthStyles(800)}
            >
                <ModalHeaderView title={title} onClose={() => vm.setShowAddOneTime(false)} />

                <div id={subtitleId} className="popup-body">
                    <NewOneTimeView onAction={() => vm.setShowAddOneTime(false)} />
                </div>
            </Modal>
        </>;
    }

    const barcodeView = () => {
        return <>
            <Modal
                titleAriaId={titleId}
                subtitleAriaId={subtitleId}
                isOpen={true}
                onDismiss={onCloseBarcode}
                isBlocking={true}
                styles={StyleUtils.getModalWidthStyles(400)}
            >
                <ModalHeaderView title="Barcode" onClose={onCloseBarcode} />

                <div id={subtitleId} className="popup-body center">
                    <Barcode
                        format={"CODE128"}
                        value={vm.barcode}
                        width={1}
                        height={30}
                        fontSize={15}
                    />
                </div>
            </Modal>
        </>;
    }

    return <div>
        {!isMobile && headerDesktopView()}
        {isMobile && headerMobileView()}

        <div className={isMobile ? "" : "pt-20"}>
            {vm.loading && <div className="pv-10"><Spinner size={SpinnerSize.large} /></div>}

            <ProductGridView
                isAdmin={true}
                isMobile={isMobile}
                lines={vm.lines}
                key={vm.listHash}
                onDetail={onDetail}
                onBarcode={onBarcode}
                addToCart={addToCart}
                setFavorite={onSetFavorite}
            />
        </div>

        <div style={{ width: "100%", display: "inline-block" }}>
            <Pager
                totalCount={vm.totalCount}
                onChange={onPageChange}
                pageSize={vm.filter.pageSize}
                key={vm.pageHash}
            />
        </div>

        {vm.showFilter && <>
            <ProductFilterView
                filter={vm.tempFilter}
                onValueChange={vm.tempSetFilterField}
                categoryList={catVM.list}
                getSelectedCategoryList={catVM.getSelectedList}
                onCategoryChecked={catVM.setCheck}
                onCategoryCollapsed={catVM.setCollapse}
                sizeOptions={vm.sizeOptions}
                onCancel={onCloseFilter}
                onFilter={onSubmitFilter}
            />
        </>}

        {vm.showAdd && addNewView()}
        {vm.showAddOneTime && addNewOneTimeView()}
        {vm.showBarcode && barcodeView()}
        {/*{showScript && this.scriptView()}*/}

        {vm.showDetail && vm.selectedProduct && <>
            <ProductDetailView
                isMobile={isMobile}
                product={vm.selectedProduct}
                service={pictureService}
                onCompletedDetail={onCompletedDetail}
            />
        </>}
    </div>;
}